import { Icon, Link, Button } from '@startlibs/components';
import { css } from 'styled-components';
import { formatDate, getColor, media } from '@startlibs/utils';
import { transparentize, lighten, rgba } from 'polished';
import React from 'react'
import styled from 'styled-components/macro';

import {RecordStatus} from '../lib/utils/recordEnums'
import {UploaderAction} from '../lib/service'
import {useToggle} from '@startlibs/core'
import { dicomDateToDate } from '../../utils';
import { dateMessages } from '../lib/utils/dateMessages';

export const RecordItemStyled = styled.div`
  background: #f0f6f8;
  border-radius: 6px;
  position: relative;
  padding: 1rem;
  & ~ & {
    margin-top: 0.75rem;
  }
  ${props => !props.isOnPanel && css`
    padding: 1.5rem;
    margin-top: 1rem;
    ${media.desktop `
      border: 1px solid #cddee3;
      padding: 0.75rem 1.5rem 0.75rem 7rem;
      min-height: 78px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `}
  `}
`
export const DicomIcon = styled(Icon).attrs({icon:'dicom'})`
  font-size: 62px;
  position: absolute;
  left: 0;
  width: 7rem;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  ${media.mobile `
    display: none;
  `}
`
export const PatientName = styled.div `
  font-weight: bold;
  font-size: 15px;
  padding-right: 2.5rem;
`
export const RecordDescription = styled.div `
  margin-top: 3px;
  font-size: 13px;
  ${props => !props.isOnPanel && `
    font-size: 12px;
    margin-top: 1px;
  `}
  ${props => props.small && `
    color: rgba(0,0,0,0.5);
    font-size: 11px;
  `}
`
export const Progress = styled.div`
  width: ${props => props.progress || 0}%;
  height: 100%;
  min-width: 4px;
  display: block;
  border-radius: 30px;
  background-color: ${getColor('main')};
  transition: 0.25s linear;
  ${props => props.completed && css`
    background-color: ${getColor('success')};
    width: 100%;
  `}
  ${props => props.failed && css`
    background-color: ${getColor('alert')};
  `}
`
export const ProgressBar = styled(({className,failed,completed,progress}) =>
  <div className={className}><Progress failed={failed} completed={completed} style={{width: (progress || "0")+"%"}}/></div>
)`
  height: 8px;
  width: 100%;
  border-radius: 30px;
  background-color: rgba(0,0,0,0.075);
  overflow: hidden;
  margin-top: 0.75rem;
  user-select: none;
`

export const UploadStatus = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  color: ${getColor('gray90')};
  ${props => props.failed && css`
    color: ${getColor('alert')};
  `}
`
export const UploadedIcon = styled(Icon)`
  font-size: 13px;
  width: 16px;
  height: 16px;
  line-height: 16px;
  padding-top: 1px;
  vertical-align: -2px;
  text-align: center;
  color: white;
  background-color: ${getColor('gray90')};
  display: inline-block;
  margin-right: 0.5rem;
  border-radius: 50%;
  font-weight: bold;
`
export const RemoveRecordIcon = styled(Icon).attrs({icon:'x'})`
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  height: 2.25rem;
  width: 2.25rem;
  text-align: center;
  line-height: 2.25rem;
  border-radius: 50%;
  background: #d9e8ed;
  color: rgba(0,0,0,0.5);
  font-size: 15px;
  cursor: pointer;
  :hover {
    background: ${props => transparentize(0.9, getColor("alert")(props))};
    color: ${getColor('alert')};
  }
  :active {
    background: ${props => transparentize(0.85, getColor("alert")(props))};
    color: ${getColor('alert')};
  }
`
const RemoveConfirmation = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  min-height: 100%;
  background: ${props => lighten(0.5, getColor("alert")(props))};
  padding: 1rem;
  border-radius: 6px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
`

export const RecordItem = ({record,doAction, openLog}) => {

  const canceling = useToggle()

  return <RecordItemStyled isOnPanel>
      <PatientName>{record.patientName}</PatientName>
      <RemoveRecordIcon onClick={(record.fileProgress.uploaded + record.fileProgress.failed) !== record.fileProgress.total ? canceling.open : (() => doAction(UploaderAction.Delete,record))}/>
      <RecordDescription isOnPanel>{record.studyModalities?.join(", ")}{record.studyModalities && " - "}{record.name} {record.studyDate && <span className='nowrap'>({formatDate(dicomDateToDate(record.studyDate), 'yyyy-MM-dd', dateMessages, true)})</span>}</RecordDescription>
      {record.status !== RecordStatus.Uploaded && <ProgressBar progress={record.progress} failed={record.status === RecordStatus.Failed}></ProgressBar>}
      <UploadStatus failed={record.status === RecordStatus.Failed}>
        <span>
          {record.status === RecordStatus.Uploaded && <UploadedIcon icon="check"/>}
          {record.status} 
          {record.status === RecordStatus.Uploading && <a className="link" css="margin-left:0.25rem" onClick={openLog}>{record.fileProgress.uploaded}/{record.fileProgress.total} files uploaded</a>}
          {record.status === RecordStatus.Failed && <a className="link" css="margin-left:0.25rem" onClick={openLog}>{record.fileProgress.failed}/{record.fileProgress.total} files failed</a>}

        </span>
        {
          record.status === RecordStatus.Failed && <a className="link" onClick={() => doAction(UploaderAction.Retry,record)}>Retry</a>
        }
        {
          record.status === RecordStatus.Waiting
          && <a className="link" onClick={() => doAction(UploaderAction.Delete,record)}>Cancel</a>
        }
        {
          record.status === RecordStatus.Uploading
          && <a className="link" onClick={() => doAction(UploaderAction.Pause,record)}>Pause</a>
        }
        {
          record.status === RecordStatus.Paused
          && <a className="link" onClick={() => doAction(UploaderAction.Retry,record)}>Resume</a>
        }
        {record.status === RecordStatus.Uploaded && <Link  className="link" to={`?page=1&studyStatus=1&forceAll=true&search=${encodeURIComponent(record.studyUID)}`}>Show study</Link>}

      </UploadStatus>
      {canceling.isOpen && <RemoveConfirmation>
        <div css="margin-bottom:auto;"><b>Do you want to stop the remaning uploads and remove this study from the upload list?</b></div>
        <div css="text-align:right;margin-top:0.5rem;">
          <Button small outline onClick={canceling.close}>Cancel</Button>
          <Button alert small onClick={() => doAction(UploaderAction.Delete,record)}>Remove</Button>
        </div>
      </RemoveConfirmation>}
  </RecordItemStyled>
}